import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import Dropdown from '@/components/Dropdown/Dropdown';
import SignatureModal from '@/components/Signature/Signature';
import CustomDateInput from '@/pages/plaintiffSheets/components/InputFields/CustomDateInput';
import {
  useGetCertificationQuery,
  useGetSasUrlQuery,
  usePutCertificationMutation,
  usePutFormSubmittedMutation,
} from '@/services/plaintiffSheets/plaintiffSheetsEndpoint';
import { getAuthData } from '@/utils/auth';
import { useToast } from '@/components/Toast/ToastContext';
import { formatToUSCentralTime } from '@/pages/plaintiffSheets/components/InputFields/USCentralTimeUtils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import CloseIcon from '@/assets/Icons/components/CloseIcon';
import { setFormSubmitted } from '@/reducers/formSubmittedSlice';
import Loader from '@/components/Loader/Loader';
import { trackEvent } from '@/utils/track-event';
import { PostHogEvents } from '@/utils/PostHogEvents';
import { api } from '@/common/config';
import useUserCollaboration from '@/hooks/useUserCollaboration';
import { useNavigate } from 'react-router-dom';

const titleOptions = [
  { label: 'Superintendent', value: 'superintendent' },
  { label: 'Interim Superintendent', value: 'interim_superintendent' },
  { label: 'Acting Superintendent', value: 'acting_superintendent' },
];

export interface CertificationsProps {
  // Existing props here
  setIsFormSubmitted?: React.Dispatch<React.SetStateAction<boolean>>;
  isPdf?: boolean;
  section_Id?: string;
  factSheetStatus?: any; // Optional prop for fact sheet status
}

export type CertificationsHandle = {
  triggerSubmit: () => void;
  setIsFormSubmitted?: (isFormSubmitted: boolean) => void;
};

interface SignatureData {
  type: string;
  value: string | null;
  file?: File | null;
  canvas?: any;
}

interface HandleSignatureSubmitData {
  type: string;
  value: string | null;
  file?: File | null;
  canvas?: any;
}

const Certifications = forwardRef<CertificationsHandle, CertificationsProps>(
  ({ setIsFormSubmitted, isPdf = false, section_Id, factSheetStatus }, ref) => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const { districtId, username, email, accessToken, role } = getAuthData();
    const [imageLoading, setImageLoading] = useState(true);
    const sectionId = useSelector((state: any) => state.sectionId.sectionId);
    const districtData = useSelector((state: RootState) => state.districtData.district);
    const [title, setTitle] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [date, setDate] = useState<string>('');

    const dispatch = useDispatch();
    const { addToast } = useToast();
    const navigate = useNavigate();
    const [updateCertification, { isLoading }] = usePutCertificationMutation();
    const [formSubmission, { isLoading: formSubmissionLoading }] = usePutFormSubmittedMutation();

    const id = isPdf ? section_Id : sectionId;
    const district_id = role === 'admin' ? districtData?.district_Id : districtId;

    const { data: certificationData } = useGetCertificationQuery(
      {
        districtId: district_id,
        sectionId: id || '',
      },
      {
        skip: !district_id || !id,
      }
    );
    const imageUrl = certificationData?.data?.signatures?.[0]?.imageUrl;

    // Only extract blobName if imageUrl is a valid non-empty string
    const isValidUrl = typeof imageUrl === 'string' && imageUrl.trim() !== '';
    const blobName = isValidUrl ? imageUrl.split('/').pop() || '' : '';

    // Use RTK query only if imageUrl is valid
    const { data: blobData } = useGetSasUrlQuery(
      {
        blobName,
        containerName: 'uploads',
      },
      {
        skip: !isValidUrl,
      }
    );

    // Call the hook at the top level with conditional connection
    const { requestToSubmit } = useUserCollaboration(`${api}/collab`, accessToken, true);

    // Set the document title on mount
    useEffect(() => {
      document.title = 'Submission | Plaintiff Fact Sheet';
    }, []);

    useEffect(() => {
      if (setIsFormSubmitted) {
        setIsFormSubmitted(isLoading);
      }
    }, [isLoading, setIsFormSubmitted]);

    useEffect(() => {
      const signatures = certificationData?.data?.signatures;

      if (signatures && signatures.length > 0) {
        const { title, date, name } = signatures[0];
        const formattedDate = formatToUSCentralTime(date);
        setName(name);
        setTitle(title || '');
        setDate(formattedDate || '');
      } else if (!name && username) {
        setName(username);
      }
    }, [certificationData, username]);

    const [signatureData, setSignatureData] = useState<SignatureData | null>(null);

    // Load signature from server if it exists
    useEffect(() => {
      if (certificationData?.data?.signatures[0]?.imageUrl) {
        // Create signature data from image URL
        setSignatureData({
          type: 'uploaded', // We assume it's an uploaded file from the server
          value: certificationData.data.signatures[0].imageUrl,
        });
      }
    }, [certificationData]);

    const handleSignatureSubmit = (data: HandleSignatureSubmitData) => {
      setSignatureData(data);
      setShowModal(false);
    };

    const handleSubmit = async () => {
      if (!signatureData) {
        addToast(
          'Signature required',
          'Please add your signature before submitting the certification.',
          'error'
        );
        return;
      }

      if (!name || !title) {
        addToast(
          'Missing information',
          'Please complete all required fields before submitting.',
          'error'
        );
        return;
      }

      try {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('title', title);

        if (signatureData.type === 'drawn' && signatureData.value) {
          try {
            const response = await fetch(signatureData.value);
            const blob = await response.blob();
            const signatureFile = new File([blob], 'drawn-signature.png', { type: 'image/png' });
            formData.append('file', signatureFile);
          } catch (err) {
            console.error('Error converting drawn signature:', err);
            addToast(
              'Signature error',
              'There was a problem processing your signature. Please try again.',
              'error'
            );
            return;
          }
        } else if (signatureData.type === 'uploaded' && signatureData.file) {
          formData.append('file', signatureData.file);
        } else {
          addToast('Signature error', 'Invalid signature data. Please try again.', 'error');
          return;
        }

        const payload = {
          name: name,
          title: title,
          file: formData.get('file'),
        };

        await updateCertification({
          districtId: districtId,
          sectionId: sectionId || '',
          payload: payload,
        });

        trackEvent(PostHogEvents.FORM_SUBMITTED, {
          form_id: sectionId,
          user_Id: email,
          timestamp: new Date().toISOString(),
        });

        addToast(
          'Progress saved successfully',
          'Progress is saved successfully. You can continue editing or can resume later to complete the form.',
          'success'
        );
        dispatch(setFormSubmitted(true));

        await formSubmission({
          districtId: districtId,
        })
          .then(() => {
            requestToSubmit();
            navigate('/user/dashboard/form-submission');
          })
          .catch((err) => {
            console.error(err);
          });
      } catch (error) {
        console.error('Error saving progress:', error);
        addToast(
          'Failed to save progress',
          'Failed to save progress. Please try again or check your connection.',
          'error'
        );
      }
    };

    useImperativeHandle(ref, () => ({
      triggerSubmit: handleSubmit,
    }));

    // Handle opening the signature modal with existing data
    const openSignatureModal = (e: React.MouseEvent) => {
      e.preventDefault();
      setShowModal(true);
    };

    const isDisabled =
      factSheetStatus === 'approved' ||
      factSheetStatus === 'submitted' ||
      factSheetStatus === 'resubmitted';

    return (
      <>
        {formSubmissionLoading ? (
          <div className="flex justify-center items-center h-[40vh] w-full">
            <Loader color="primary" size="xl" />
          </div>
        ) : (
          <div className="space-y-6 w-full">
            {/* Statement */}
            <div className="pb-4 pt-2 space-y-2 border-b border-ds-gray-100">
              <h3 className="text-base font-semibold text-ds-gray-800">
                I have made reasonable inquiries to answer the foregoing questions. Based on my
                personal knowledge and the information provided by other district employees, I
                declare under penalty of perjury that the information provided in this Plaintiff
                Fact Sheet is complete, true, and correct to the best of my knowledge and
                information, and that I have provided all of the requested documents that are
                reasonably accessible to me and/or my attorneys, to the best of my knowledge.
              </h3>
            </div>

            <div className="flex gap-6  ">
              {/* Signature */}
              <div className="flex flex-col mt-5 flex-1 gap-2">
                <label className="text-xs font-semibold text-ds-gray-500 mb-1">Signature</label>
                {certificationData?.data?.signatures[0]?.imageUrl ? (
                  <div className="flex flex-col space-y-1">
                    {/* Do not delete for view once the form is submitted */}
                    {/* <a
                  href="#"
                  onClick={openSignatureModal}
                  className="text-xs font-normal text-ds-purple-600 underline"
                >
                  View Signature 
                </a> */}
                    <div className="border-2 text-xs p-2 font-normal focus:ring-0 outline-none border-ds-gray-100 rounded overflow-hidden w-32">
                      {imageLoading && (
                        <div className="h-8 flex items-center justify-center">
                          <Loader color="primary" size="sm" />
                        </div>
                      )}
                      <img
                        src={blobData?.data?.url}
                        alt="Uploaded content"
                        className={`w-full h-auto ${imageLoading ? 'hidden' : ''}`}
                        onLoad={() => setImageLoading(false)}
                        onError={() => setImageLoading(false)}
                      />
                    </div>
                  </div>
                ) : (
                  <a
                    href="#"
                    onClick={isDisabled ? (e) => e.preventDefault() : openSignatureModal}
                    className={`text-xs font-normal text-ds-purple-600 ${isDisabled ? 'pointer-events-none opacity-50' : ''}`}
                    tabIndex={isDisabled ? -1 : 0}
                    aria-disabled={isDisabled}
                  >
                    {signatureData?.value ? (
                      <div className="flex gap-2 items-center underline">
                        Signature Added{' '}
                        <div className="underline">
                          <CloseIcon width="7" height="7" />
                        </div>
                      </div>
                    ) : (
                      '+ Add Signature'
                    )}
                  </a>
                )}
                {signatureData && !certificationData?.data?.signatures[0]?.imageUrl && (
                  <div className="text-xs text-ds-gray-600">
                    {signatureData.type === 'drawn'
                      ? 'Hand-drawn signature added'
                      : 'Uploaded signature added'}
                  </div>
                )}
                {showModal && (
                  <SignatureModal
                    onClose={() => setShowModal(false)}
                    onSignatureSubmit={handleSignatureSubmit}
                    initialSignature={signatureData}
                  />
                )}
              </div>

              {/* Date */}
              <div className="flex flex-col w-full flex-1 gap-2">
                <CustomDateInput
                  label="Date"
                  placeholder="MM/DD/YYYY, HH:MM AM/PM CST/CDT"
                  onChange={(e) => setDate(e.target.value)}
                  value={date}
                  isEditable={false}
                  name="Date"
                  width="w-full"
                  className="text-gray-2250 text-xs w-full "
                  showRunningTime={!certificationData?.data?.signatures[0]?.imageUrl}
                />
              </div>
            </div>
            {/* Name and Title */}
            <div className="flex gap-6">
              {/* Name */}
              <div className="flex flex-col flex-1 gap-2">
                <label className="text-xs font-semibold text-ds-gray-500 mb-1">Name</label>
                <input
                  type="text"
                  value={name || ''}
                  placeholder="Enter name"
                  disabled={isDisabled}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10 border-2 rounded px-2 text-xs font-normal focus:ring-0 outline-none border-ds-gray-100 text-ds-gray-800 submission"
                />
              </div>

              {/* Title */}
              <div className="flex flex-col flex-1 gap-2">
                <label className="text-xs font-semibold text-ds-gray-500 mb-1">Title</label>
                <Dropdown
                  options={titleOptions}
                  selectedValue={title}
                  onChange={setTitle}
                  isFullWidth={true}
                  isDisabled={isDisabled}
                />
              </div>
            </div>
            {/* Note */}
            <div className="bg-ds-orange-100 p-4 rounded">
              <p className="text-xs text-ds-orange-900 legal-note-print">
                <strong>Note:</strong> This submission should be made by the superintendent or
                another individual in an equivalent role.
              </p>
            </div>
          </div>
        )}
      </>
    );
  }
);

Certifications.displayName = 'Certifications';
export default Certifications;
